/**
 * Covert WiFi Speed Monitor
 * Runs background speed tests when user connects to a known venue's WiFi
 * Updates venue speed data transparently
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Check interval in milliseconds (check every 5 minutes)
const CHECK_INTERVAL = 5 * 60 * 1000;

// Storage key for last test timestamps
const LAST_TEST_KEY = 'strider_wifi_tests';

/**
 * Get venue's estimated network names from address/name
 */
function getExpectedNetworkPatterns(venue) {
  const patterns = [];
  const name = venue.name?.toLowerCase() || '';
  const address = venue.location?.address?.toLowerCase() || '';
  
  // Extract potential WiFi network name patterns
  const words = name.split(/[\s\-_]+/).filter(w => w.length > 2);
  words.forEach(word => {
    patterns.push(word);
  });
  
  // Add common variations
  if (name) {
    patterns.push(name.replace(/\s+/g, ''));
    patterns.push(name.replace(/\s+/g, '_'));
    patterns.push(name.replace(/\s+/g, '-'));
  }
  
  return patterns;
}

/**
 * Check if current network might match a venue
 */
async function checkNetworkMatch(venue) {
  // Use Network Information API if available
  if (!('connection' in navigator)) {
    return false;
  }
  
  // We can't directly get SSID in browsers for privacy
  // But we can use location + connection quality heuristics
  
  // Check if user is likely at the venue location
  if ('geolocation' in navigator) {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const venueLat = venue.location?.lat;
          const venueLng = venue.location?.lng;
          
          if (!venueLat || !venueLng) {
            resolve(false);
            return;
          }
          
          // Calculate distance (rough approximation)
          const distance = Math.sqrt(
            Math.pow(latitude - venueLat, 2) + 
            Math.pow(longitude - venueLng, 2)
          ) * 111000; // Convert to meters
          
          // If within 100 meters of venue, consider it a match
          resolve(distance < 100);
        },
        () => resolve(false),
        { timeout: 5000, enableHighAccuracy: false }
      );
    });
  }
  
  return false;
}

/**
 * Run a speed test and submit to backend
 * Uses larger files and multiple samples for accuracy
 */
async function runCovertSpeedTest(venueId) {
  try {
    // Test files of various sizes
    const testFiles = [
      { url: 'https://cdn.jsdelivr.net/npm/jquery@3.7.1/dist/jquery.min.js', size: 87000 },
      { url: 'https://cdn.jsdelivr.net/npm/lodash@4.17.21/lodash.min.js', size: 72000 },
      { url: 'https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/js/bootstrap.bundle.min.js', size: 80000 },
      { url: 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js', size: 200000 },
    ];
    
    let totalBytes = 0;
    let totalTimeMs = 0;
    const samples = [];
    
    // Run 2 rounds for better accuracy
    for (let round = 0; round < 2; round++) {
      for (const file of testFiles) {
        try {
          const url = file.url + '?t=' + Date.now() + '_' + Math.random();
          const startTime = performance.now();
          const response = await fetch(url, { cache: 'no-store', mode: 'cors' });
          const data = await response.arrayBuffer();
          const endTime = performance.now();
          
          const durationMs = endTime - startTime;
          const bytes = data.byteLength;
          const speedMbps = (bytes * 8 / (durationMs / 1000)) / 1000000;
          
          totalBytes += bytes;
          totalTimeMs += durationMs;
          samples.push(speedMbps);
        } catch (err) {
          // Speed test sample failed - expected for some requests
          console.debug("Speed sample skipped:", err.message);
        }
      }
    }
    
    if (samples.length === 0) return null;
    
    // Calculate overall speed (most accurate method)
    const downloadMbps = (totalBytes * 8 / (totalTimeMs / 1000)) / 1000000;
    
    // Estimate upload based on connection type
    let uploadMbps = downloadMbps * 0.25;
    let latencyMs = 30;
    
    if ('connection' in navigator) {
      const conn = navigator.connection;
      if (conn.downlink) {
        uploadMbps = conn.downlink * 0.3;
      }
      if (conn.rtt) {
        latencyMs = conn.rtt;
      }
    }
    
    // Latency test with more samples
    const latencies = [];
    for (let i = 0; i < 5; i++) {
      try {
        const start = performance.now();
        await fetch('https://cdn.jsdelivr.net/npm/lodash@4.17.21/package.json?ping=' + Date.now() + '_' + i, {
          cache: 'no-store'
        });
        latencies.push(performance.now() - start);
      } catch (e) {
        // Latency test sample failed - expected for some requests
        console.debug("Latency sample skipped:", e.message);
      }
    }
    
    if (latencies.length > 0) {
      // Remove outliers and average
      const sorted = latencies.sort((a, b) => a - b);
      const trimmed = sorted.length > 2 ? sorted.slice(1, -1) : sorted;
      latencyMs = trimmed.reduce((a, b) => a + b, 0) / trimmed.length;
    }
    
    // Detect time of day
    const hour = new Date().getHours();
    let timeOfDay = 'night';
    if (hour >= 5 && hour < 12) timeOfDay = 'morning';
    else if (hour >= 12 && hour < 17) timeOfDay = 'afternoon';
    else if (hour >= 17 && hour < 21) timeOfDay = 'evening';
    
    // Submit to backend
    const response = await fetch(`${API}/venues/${venueId}/wifi-speed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        download_mbps: Math.round(downloadMbps * 10) / 10,
        upload_mbps: Math.round(uploadMbps * 10) / 10,
        latency_ms: Math.round(latencyMs),
        device_type: detectDeviceType(),
        time_of_day: timeOfDay
      })
    });
    
    const result = await response.json();
    
    // Store last test time in localStorage (non-sensitive, just timestamps for rate-limiting)
    // This prevents excessive testing of the same venue
    const lastTests = JSON.parse(localStorage.getItem(LAST_TEST_KEY) || '{}');
    lastTests[venueId] = Date.now();
    localStorage.setItem(LAST_TEST_KEY, JSON.stringify(lastTests));
    
    return result;
  } catch (error) {
    // Covert tests should fail silently - non-critical background operation
    console.debug("Covert WiFi test skipped:", error.message);
    return null;
  }
}

function detectDeviceType() {
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipad|ipod/.test(ua)) {
    if (/ipad|tablet/.test(ua)) return 'tablet';
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Check if we should run a test for this venue
 */
function shouldRunTest(venueId) {
  const lastTests = JSON.parse(localStorage.getItem(LAST_TEST_KEY) || '{}');
  const lastTest = lastTests[venueId];
  
  if (!lastTest) return true;
  
  // Don't test same venue more than once per hour
  const oneHour = 60 * 60 * 1000;
  return (Date.now() - lastTest) > oneHour;
}

/**
 * Initialize covert WiFi monitoring for a venue page
 */
export async function initCovertWiFiMonitor(venue) {
  if (!venue || !venue.id) return;
  
  // Only run if we haven't tested recently
  if (!shouldRunTest(venue.id)) {
    return;
  }
  
  // Check if user might be at this venue
  const isAtVenue = await checkNetworkMatch(venue);
  
  if (isAtVenue) {
    // Run test after a short delay to not impact page load
    setTimeout(() => {
      runCovertSpeedTest(venue.id);
    }, 3000);
  }
}

/**
 * Track page view with optional covert WiFi check
 */
export async function trackVenueVisit(venueId) {
  try {
    await fetch(`${API}/venues/${venueId}/track-view`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' }
    });
  } catch (error) {
    // Analytics tracking is non-critical - continue without disrupting user experience
    console.debug("View tracking skipped:", error.message);
  }
}

/**
 * Batch WiFi test for nearby venues (runs when user grants location)
 */
export async function runNearbyVenueTests(userLat, userLng, nearbyVenues) {
  for (const venue of nearbyVenues) {
    if (!venue.id) continue;
    if (!shouldRunTest(venue.id)) continue;
    
    const venueLat = venue.location?.lat;
    const venueLng = venue.location?.lng;
    
    if (!venueLat || !venueLng) continue;
    
    // Check if within 100m
    const distance = Math.sqrt(
      Math.pow(userLat - venueLat, 2) + 
      Math.pow(userLng - venueLng, 2)
    ) * 111000;
    
    if (distance < 100) {
      // Stagger tests to avoid overwhelming
      await new Promise(r => setTimeout(r, 1000));
      await runCovertSpeedTest(venue.id);
    }
  }
}

export default {
  initCovertWiFiMonitor,
  trackVenueVisit,
  runNearbyVenueTests,
  runCovertSpeedTest
};
